import { useEffect, useLayoutEffect } from 'react'

import { findScrollableAncestor } from '@root/shared/lib/dom/find-scrollable-ancestor'
import {
  forwardWheelToMarquee,
  normalizeWheelDelta,
  useMarqueeWheelRoot,
} from '@root/shared/ui-kit/marquee/marquee-wheel'

export { findScrollableAncestor } from '@root/shared/lib/dom/find-scrollable-ancestor'
export { normalizeWheelDelta } from '@root/shared/ui-kit/marquee/marquee-wheel'

const applyVerticalWheelScroll = (
  scrollable: Element | Window,
  dy: number,
): void => {
  if (scrollable instanceof Window) {
    scrollable.scrollBy({ top: dy, left: 0 })
    return
  }
  scrollable.scrollTop += dy
}

/**
 * @deprecated
 * On `overflow-x: auto` containers, blocks vertical-dominant wheel from scrolling
 * horizontally while still allowing horizontal-dominant wheel / native scroll.
 */
export function useBlockVerticalWheelOnHorizontalScroller(
  ref: React.RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }

    const onWheel = (event: WheelEvent) => {
      if (event.defaultPrevented) {
        return
      }
      const target = event.target
      if (!(target instanceof Node) || !el.contains(target)) {
        return
      }

      const dy = normalizeWheelDelta(event.deltaY, event.deltaMode)
      const dx = normalizeWheelDelta(event.deltaX, event.deltaMode)
      if (Math.abs(dy) <= Math.abs(dx) || dy === 0) {
        return
      }

      event.preventDefault()
      const scrollable = findScrollableAncestor(el)
      if (scrollable) {
        applyVerticalWheelScroll(scrollable, dy)
      }
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [ref])
}

/**
 * Forwards wheel from portaled popover content to the page (vertical) or
 * marquee (horizontal).
 *
 * The listener is attached to the popover body element rather than `document`
 * so it only runs for wheel events over the popover. A `passive: false`
 * listener on `document` would add latency to every wheel event globally
 * (the browser must wait for JS before scrolling), which is very noticeable
 * on heavy pages like ExplorePage.
 */
export function useForwardWheelFromPopoverWhileOpen(options: {
  open: boolean
  triggerRef: React.RefObject<Element | null>
  contentRef: React.RefObject<HTMLElement | null>
  nestedOverlayOpenRef?: React.RefObject<boolean>
}): void {
  const { open, triggerRef, contentRef, nestedOverlayOpenRef } = options
  const marqueeRoot = useMarqueeWheelRoot()

  useLayoutEffect(() => {
    if (!open) {
      return
    }
    const body = contentRef.current
    if (!body) {
      return
    }

    const scrollable = findScrollableAncestor(triggerRef.current)

    const onWheel = (event: WheelEvent) => {
      if (nestedOverlayOpenRef?.current) {
        return
      }

      const dy = normalizeWheelDelta(event.deltaY, event.deltaMode)
      const dx = normalizeWheelDelta(event.deltaX, event.deltaMode)

      if (marqueeRoot && (dx !== 0 || dy !== 0)) {
        event.preventDefault()
        forwardWheelToMarquee(marqueeRoot, event)
        return
      }

      if (dy !== 0 && scrollable) {
        event.preventDefault()
        applyVerticalWheelScroll(scrollable, dy)
      }
    }

    body.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      body.removeEventListener('wheel', onWheel)
    }
  }, [contentRef, marqueeRoot, nestedOverlayOpenRef, open, triggerRef])
}
